import React, { useState, useRef, useEffect } from 'react';
import { Info } from 'lucide-react';

/**
 * Tooltip informativo con ícono ⓘ
 * Muestra definición/fórmula al hacer hover Y click
 * Posicionamiento inteligente para evitar salirse del viewport
 */
export function InfoTooltip({ text, className = '' }) {
  const [show, setShow] = useState(false);
  const [position, setPosition] = useState('top');
  const tooltipRef = useRef(null);
  const iconRef = useRef(null);

  // Cerrar con click fuera
  useEffect(() => {
    if (show) {
      const handleClickOutside = (e) => {
        if (tooltipRef.current && !tooltipRef.current.contains(e.target) &&
            iconRef.current && !iconRef.current.contains(e.target)) {
          setShow(false);
        }
      };
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [show]);

  // Calcular mejor posición
  useEffect(() => {
    if (show && iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      
      if (spaceAbove < 100 && spaceBelow > spaceAbove) {
        setPosition('bottom');
      } else {
        setPosition('top');
      }
    }
  }, [show]);

  const handleToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShow(!show);
  };

  const positionStyles = position === 'top' 
    ? 'bottom-full mb-2' 
    : 'top-full mt-2';
    
  const arrowStyles = position === 'top'
    ? 'top-full border-t-gray-800'
    : 'bottom-full border-b-gray-800';

  return (
    <span 
      ref={iconRef}
      className={`relative inline-flex items-center ${className}`}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={handleToggle}
    >
      <Info 
        className="w-3.5 h-3.5 text-gray-400 hover:text-blue-600 cursor-help ml-1 transition-colors" 
        data-testid="info-tooltip-icon"
      />
      {show && text && (
        <div 
          ref={tooltipRef}
          className={`absolute z-[9999] ${positionStyles} left-1/2 -translate-x-1/2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg shadow-xl max-w-[280px] whitespace-normal text-left pointer-events-auto`}
          style={{ minWidth: '180px' }}
          data-testid="info-tooltip-content"
        >
          {text}
          <div className={`absolute ${arrowStyles} left-1/2 -translate-x-1/2 border-4 border-transparent`}></div>
        </div>
      )}
    </span>
  );
}

/**
 * KPI Card con tooltip integrado
 */
export function KPICardWithTooltip({ 
  title, 
  value, 
  subtitle, 
  tooltip, 
  colorClass = 'from-gray-50 to-white border-gray-200',
  textColorClass = 'text-gray-700',
  titleColorClass = 'text-gray-600'
}) {
  return (
    <div className={`bg-gradient-to-br ${colorClass} rounded-xl border p-4`}>
      <div className="flex items-center gap-1">
        <p className={`text-sm font-medium ${titleColorClass}`}>{title}</p>
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <p className={`text-3xl font-bold ${textColorClass}`}>{value}</p>
      {subtitle && <p className={`text-xs ${titleColorClass} mt-1`}>{subtitle}</p>}
    </div>
  );
}

/**
 * Encabezado de sección con descripción
 */
export function SectionHeader({ title, description, icon: Icon }) {
  return (
    <div className="mb-4">
      <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
        {Icon && <Icon className="w-5 h-5" />}
        {title}
      </h2>
      {description && (
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      )}
    </div>
  );
}

/**
 * Panel de definiciones (pie de página o expandible)
 */
export function DefinitionsPanel({ definitions, className = '' }) {
  const [expanded, setExpanded] = useState(false);

  if (!definitions || Object.keys(definitions).length === 0) return null;

  const entries = Object.entries(definitions);
  const visibleEntries = expanded ? entries : entries.slice(0, 3);

  return (
    <div className={`bg-gray-50 rounded-lg p-3 text-xs text-gray-600 ${className}`}>
      <div className="flex items-center gap-1 mb-2">
        <Info className="w-3.5 h-3.5" />
        <span className="font-medium">Definiciones</span>
      </div>
      <div className="space-y-1">
        {visibleEntries.map(([key, value]) => (
          <div key={key}>
            <span className="font-medium text-gray-700">{key}:</span>{' '}
            <span>{value}</span>
          </div>
        ))}
      </div>
      {entries.length > 3 && (
        <button 
          onClick={() => setExpanded(!expanded)}
          className="text-blue-600 hover:underline mt-2"
        >
          {expanded ? 'Ver menos' : `Ver ${entries.length - 3} más`}
        </button>
      )}
    </div>
  );
}

/**
 * Estado vacío con mensaje claro
 */
export function EmptyState({ title, message, icon: Icon }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-gray-500">
      {Icon && <Icon className="w-12 h-12 text-gray-300 mb-3" />}
      <p className="font-medium text-gray-600">{title}</p>
      {message && <p className="text-sm mt-1">{message}</p>}
    </div>
  );
}

export default { InfoTooltip, KPICardWithTooltip, SectionHeader, DefinitionsPanel, EmptyState };
